import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Settings, Info } from 'lucide-react';
import { GSTTreatment, getGSTTreatmentLabel, IndianStateItem } from '../../lib/gst-rules';

interface GSTSettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentTreatment: GSTTreatment;
  onTreatmentChange: (treatment: GSTTreatment) => void;
  supplierGSTIN?: string;
  autoDetectedTreatment?: GSTTreatment;
  supplierAddress?: string;
  /** Supplier address state ID from indianStates (e.g. 12, 27, etc.) */
  supplierStateId?: number | string;
  /** Indian states list (id, state_name, isUnionTerritories). Used to show state names and for treatment logic. */
  indianStates?: IndianStateItem[];
  /** Our company state id in indianStates (e.g. 12 for Karnataka). */
  ourStateId?: number | string;
}

export const GSTSettingsDialog: React.FC<GSTSettingsDialogProps> = ({
  isOpen,
  onClose,
  currentTreatment,
  onTreatmentChange,
  supplierGSTIN,
  supplierAddress,
  supplierStateId,
  autoDetectedTreatment,
  indianStates = [],
  ourStateId = 12,
}) => {
  const supplierState = supplierStateId && indianStates.length 
    ? indianStates.find((s) => String(s.id) === String(supplierStateId))
    : null;

  const ourState = indianStates.find((s) => String(s.id) === String(ourStateId));
  const companyStateName = ourState?.state_name ?? 'Karnataka';
  const companyStateId = ourState?.id ?? 12;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            GST Treatment Settings
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Auto-detection Info */}
          {supplierStateId && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Auto-Detection Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Company State:</span>
                    <div className="text-muted-foreground">{companyStateName} </div>
                  </div>
                  <div>
                    <span className="font-medium">Supplier State:</span>
                    <div className="text-muted-foreground">
                      {supplierState?.state_name || (supplierStateId ? `${supplierStateId}` : 'Unknown')}
                    </div>
                  </div>
                </div>
                {autoDetectedTreatment && (
                  <div className="pt-2 border-t">
                    <span className="font-medium text-sm">Recommended Treatment:</span>
                    <Badge variant="secondary" className="ml-2">
                      {getGSTTreatmentLabel(autoDetectedTreatment)}
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* GST Treatment Selection */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Select GST Treatment</Label>
            <RadioGroup 
              value={currentTreatment} 
              onValueChange={(value: string) => onTreatmentChange(value as GSTTreatment)}
              className="space-y-3"
            >
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50">
                <RadioGroupItem value="cgst_sgst" id="cgst_sgst" />
                <div className="flex-1">
                  <Label htmlFor="cgst_sgst" className="font-medium cursor-pointer">
                    CGST + SGST
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    For transactions within the same state
                  </p>
                </div>
                {currentTreatment === 'cgst_sgst' && (
                  <Badge variant="default">Selected</Badge>
                )}
              </div>
              
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50">
                <RadioGroupItem value="igst" id="igst" />
                <div className="flex-1">
                  <Label htmlFor="igst" className="font-medium cursor-pointer">
                    IGST
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    For inter-state transactions
                  </p>
                </div>
                {currentTreatment === 'igst' && (
                  <Badge variant="default">Selected</Badge>
                )}
              </div>
              
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50">
                <RadioGroupItem value="cgst_utgst" id="cgst_utgst" />
                <div className="flex-1">
                  <Label htmlFor="cgst_utgst" className="font-medium cursor-pointer">
                    CGST + UTGST
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    For transactions involving Union Territories
                  </p>
                </div>
                {currentTreatment === 'cgst_utgst' && (
                  <Badge variant="default">Selected</Badge>
                )}
              </div>
            </RadioGroup>
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={onClose}>
              Apply Settings
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};